import Chip from 'components/Chip'
import ContentBox from 'components/ContentBox'
import Row from 'components/Row.js'
import TextBody from 'components/TextBody'
import TextTitleCondensed from 'components/TextTitleCondensed'
import { View } from 'components/View'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import {
  isMobileScreenViewportSelect,
  isSmallMobileScreenViewportSelect
} from 'modules/viewport/selectors'
import PropTypes from 'prop-types'
import React from 'react'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import {
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_PRICE,
  SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE
} from '@gymaholic/packages'
import { getSelectedSubscriptionPlanSelect } from 'modules/training/modules/training-premium/selectors/get-selected-subscription-plan/index.js'
import { SubscriptionPremiumPlusPlanFeatures } from 'pages/Premium/FreeVsPremiumFeatures/SubscriptionPlans/PremiumPlus/Features/index.js'
import { TryPremiumPlusFreeButton } from 'components/TryPremiumPlusFreeButton/index.js'
import { isYearlySubscriptionUtil } from 'modules/training/modules/subscription/utils/is-yearly-subscription/index.js'
import { hasPremiumPlusUserSelect } from 'modules/training/modules/training-users/selectors/has-premium-plus/index.js'
import { TopRightOverlay } from 'components/TopRightOverlay/index.js'
import TextBodySmaller from 'components/TextBodySmaller.js'
import { hasFreeTrialEndedUserSelect } from 'modules/training/modules/training-users/selectors/has-free-trial-ended/index.js'
import { hasPremiumUserSelect } from 'modules/training/modules/training-users/selectors/has-premium/index.js'
import { shouldShowUpgradeDiscountUserSelect } from 'modules/training/modules/training-users/selectors/should-show-upgrade-discount/index.js'
import { NewCustomerDiscountTimer } from 'pages/Premium/FreeVsPremiumFeatures/SubscriptionPlanPicker/NewCustomerDiscountTimer/index.js'
import { ExclusiveUpgradeOffer } from 'pages/Premium/FreeVsPremiumFeatures/SubscriptionPlans/PremiumPlus/ExclusiveUpgradeOffer/index.js'
import { shouldShowNewCustomerDiscountUserSelect } from 'modules/training/modules/training-users/selectors/shouldShowNewCustomerDiscount/index.js'
import {
  SUBSCRIPTION_PLAN_MONTHLY_SAVINGS,
  SUBSCRIPTION_PLAN_YEARLY_SAVINGS
} from 'modules/subscription/constants/index.js'
import { MostPopular } from 'pages/Premium/FreeVsPremiumFeatures/SubscriptionPlans/PremiumPlus/MostPopular/index.js'
import TextBodySmall from 'components/TextBodySmall/index.js'

const messages = defineMessages({
  title: {
    id: 'SubscriptionPremiumPlusPlan.title',
    defaultMessage: 'Premium plus 💎'
  },
  yourPlan: {
    id: 'SubscriptionPremiumPlusPlan.yourPlan',
    defaultMessage: 'Your plan'
  },
  freeTrialCancelAnytime: {
    id: 'SubscriptionPremiumPlusPlan.freeTrialCancelAnytime',
    defaultMessage: '$0 due today. 7-day free trial. Cancel any time.'
  },
  freeTrialCancelAnytimeTrialEnded: {
    id: 'SubscriptionPremiumPlusPlan.freeTrialCancelAnytimeTrialEnded',
    defaultMessage: 'Start your subscription today. Cancel any time.'
  },
  cancelAnytime: {
    id: 'SubscriptionPremiumPlusPlan.cancelAnytime',
    defaultMessage: 'Cancel any time.'
  },
  saveAmountPerYear: {
    id: 'SubscriptionPremiumPlusPlan.saveAmountPerYear',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: 'Save ${amountSavedPerYear}/year'
  },
  saveAmountPerMonth: {
    id: 'SubscriptionPremiumPlusPlan.saveAmountPerMonth',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: 'Save ${amountSavedPerMonth}/month'
  },
  monthlyPrice: {
    id: 'SubscriptionPremiumPlusPlan.monthlyPrice',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${monthlyPrice} / month'
  },
  priceWithoutDiscount: {
    id: 'SubscriptionPremiumPlusPlan.priceWithoutDiscount',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${priceWithoutDiscount}'
  },
  priceYearlyPerMonth: {
    id: 'SubscriptionPremiumPlusPlan.priceYearlyPerMonth',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${monthlyPrice} / month'
  },
  priceYearly: {
    id: 'SubscriptionPremiumPlusPlan.priceYearly',
    // eslint-disable-next-line no-template-curly-in-string
    defaultMessage: '${yearlyPrice} / year'
  },
  bestFor: {
    id: 'SubscriptionPremiumPlusPlan.bestFor',
    defaultMessage: 'For those tracking both workouts and nutrition.'
  }
})

/**
 * @param props
 * @param props.isSummary
 * @param props.isReadOnly
 * @param props.isCompact
 * @param props.areFeaturesPressable
 * @param props.isStandalone
 * @param props.shouldBoldMainFeatures
 * @return {Element}
 * @constructor
 */
export function SubscriptionPremiumPlusPlan (props) {
  const {
    isSummary,
    isCompact,
    areFeaturesPressable,
    isStandalone,
    shouldBoldMainFeatures
  } = props

  const hasPremium = useSelector(hasPremiumUserSelect)
  const hasPremiumPlus = useSelector(hasPremiumPlusUserSelect)
  const selectedPlan = useSelector(getSelectedSubscriptionPlanSelect)
  const isMobileScreen = useSelector(isMobileScreenViewportSelect)
  const intl = useIntl()
  const hasFreeTrialEnded = useSelector(hasFreeTrialEndedUserSelect)
  const isSmallMobileScreen = useSelector(isSmallMobileScreenViewportSelect)
  const isYearly = isYearlySubscriptionUtil(selectedPlan)
  const isLandingPage = !selectedPlan
  const shouldShowUpgradeDiscount = useSelector(shouldShowUpgradeDiscountUserSelect)
  const shouldShowNewCustomerDiscount = useSelector(shouldShowNewCustomerDiscountUserSelect)

  // TODO move to selector
  let priceWithoutDiscount
  let yearlyPrice
  let monthlyPrice
  let priceLabelElement
  let priceWithoutDiscountElement
  let priceSuffixElement
  let amountSavedPerYear
  let amountSavedPerMonth
  if (
    shouldShowUpgradeDiscount &&
    (
      isYearly ||
      isLandingPage
    )
  ) {
    priceWithoutDiscount = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_2025]
    const subscriptionPlan = SUBSCRIPTION_PLAN.PREMIUM_PLUS_YEARLY_UPGRADE_2025
    yearlyPrice = SUBSCRIPTION_PLAN_PRICE[subscriptionPlan]
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[subscriptionPlan].toFixed(2)
    amountSavedPerYear = SUBSCRIPTION_PLAN_YEARLY_SAVINGS[subscriptionPlan].toFixed(2)
    priceLabelElement = formatMessageIntlUtil(intl, messages.priceYearlyPerMonth, { monthlyPrice })
    priceWithoutDiscountElement = (
      <View mr>
        <s>
          <TextBodySmaller>
            {formatMessageIntlUtil(intl, messages.priceWithoutDiscount, { priceWithoutDiscount })}
          </TextBodySmaller>
        </s>
      </View>
    )
    priceSuffixElement = (
      <Chip ml>
        {formatMessageIntlUtil(intl, messages.priceYearly, { yearlyPrice })}
      </Chip>
    )
  } else if (
    shouldShowUpgradeDiscount &&
    !isYearly
  ) {
    priceWithoutDiscount = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_2025]
    const subscriptionPlan = SUBSCRIPTION_PLAN.PREMIUM_PLUS_UPGRADE_2025
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[subscriptionPlan].toFixed(2)
    priceLabelElement = formatMessageIntlUtil(intl, messages.priceYearlyPerMonth, { monthlyPrice })
    amountSavedPerMonth = SUBSCRIPTION_PLAN_MONTHLY_SAVINGS[subscriptionPlan].toFixed(2)
    priceWithoutDiscountElement = (
      <View mr>
        <s>
          <TextBodySmaller>
            {formatMessageIntlUtil(intl, messages.priceWithoutDiscount, { priceWithoutDiscount })}
          </TextBodySmaller>
        </s>
      </View>
    )
  } else if (
    shouldShowNewCustomerDiscount &&
    (
      isYearly ||
      isLandingPage
    )
  ) {
    priceWithoutDiscount = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_2025]
    const subscriptionPlan = SUBSCRIPTION_PLAN.PREMIUM_PLUS_YEARLY_SALE_2025
    yearlyPrice = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_YEARLY_SALE_2025]
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_YEARLY_SALE_2025].toFixed(2)
    amountSavedPerYear = SUBSCRIPTION_PLAN_YEARLY_SAVINGS[subscriptionPlan].toFixed(2)
    priceLabelElement = formatMessageIntlUtil(intl, messages.priceYearlyPerMonth, { monthlyPrice })
    priceWithoutDiscountElement = (
      <View mr>
        <s>
          <TextBodySmall>
            {formatMessageIntlUtil(intl, messages.priceWithoutDiscount, { priceWithoutDiscount })}
          </TextBodySmall>
        </s>
      </View>
    )
    priceSuffixElement = (
      <Chip ml>
        {formatMessageIntlUtil(intl, messages.priceYearly, { yearlyPrice })}
      </Chip>
    )
  } else if (
    shouldShowNewCustomerDiscount &&
    !isYearly
  ) {
    priceWithoutDiscount = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_2025]
    const subscriptionPlan = SUBSCRIPTION_PLAN.PREMIUM_PLUS_SALE_2025
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_SALE_2025].toFixed(2)
    priceLabelElement = formatMessageIntlUtil(intl, messages.priceYearlyPerMonth, { monthlyPrice })
    amountSavedPerMonth = SUBSCRIPTION_PLAN_MONTHLY_SAVINGS[subscriptionPlan].toFixed(2)
    priceWithoutDiscountElement = (
      <View mr>
        <s>
          <TextBodySmall>
            {formatMessageIntlUtil(intl, messages.priceWithoutDiscount, { priceWithoutDiscount })}
          </TextBodySmall>
        </s>
      </View>
    )
  } else if (isLandingPage) {
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_YEARLY_2025]
    priceLabelElement = formatMessageIntlUtil(intl, messages.monthlyPrice, { monthlyPrice })
  } else if (isYearly) {
    priceWithoutDiscount = SUBSCRIPTION_PLAN_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_2025]
    const subscriptionPlan = SUBSCRIPTION_PLAN.PREMIUM_PLUS_YEARLY_2025
    yearlyPrice = SUBSCRIPTION_PLAN_PRICE[subscriptionPlan]
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[subscriptionPlan]
    amountSavedPerYear = SUBSCRIPTION_PLAN_YEARLY_SAVINGS[subscriptionPlan].toFixed(2)
    priceLabelElement = formatMessageIntlUtil(intl, messages.priceYearlyPerMonth, { monthlyPrice })
    priceWithoutDiscountElement = (
      <View mr>
        <s>
          <TextBodySmall>
            {formatMessageIntlUtil(intl, messages.priceWithoutDiscount, { priceWithoutDiscount })}
          </TextBodySmall>
        </s>
      </View>
    )
    priceSuffixElement = (
      <Chip ml>
        {formatMessageIntlUtil(intl, messages.priceYearly, { yearlyPrice })}
      </Chip>
    )
  } else {
    monthlyPrice = SUBSCRIPTION_PLAN_TO_MONTHLY_PRICE[SUBSCRIPTION_PLAN.PREMIUM_PLUS_2025]
    priceLabelElement = formatMessageIntlUtil(intl, messages.monthlyPrice, { monthlyPrice })
  }

  const style = {
    position: 'relative',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)'
  }
  if (isMobileScreen) {
    style.width = 'calc(100vw - 5rem)'
  } else {
    style.width = '18em'
  }

  let freeTrialMessage
  if (hasPremium) {
    freeTrialMessage = messages.cancelAnytime
  } else if (hasFreeTrialEnded) {
    freeTrialMessage = messages.freeTrialCancelAnytimeTrialEnded
  } else {
    freeTrialMessage = messages.freeTrialCancelAnytime
  }

  return (
    <ContentBox
      pt={isCompact ? 2 : 3}
      pb={2}
      mb={0}
      style={style}
      bd={isCompact}
    >
      <View
        style={{
          alignItems: isSummary ? View.ALIGN.START : View.ALIGN.CENTER,
          justifyContent: View.JUSTIFY.CENTER,
          width: '100%',
          height: '100%'
        }}
      >
        {
          !!hasPremiumPlus && (
            <TopRightOverlay>
              <span>{formatMessageIntlUtil(intl, messages.yourPlan)}</span>
            </TopRightOverlay>
          )
        }

        {!hasPremiumPlus && !isStandalone && <MostPopular />}
        <ExclusiveUpgradeOffer />
        {/* <LimitedTimeOffer /> */}

        <h3 style={{ height: '1.7em' }}>
          <TextTitleCondensed style={{
            display: 'inline-block',
            height: '100%'
          }}
          >
            {formatMessageIntlUtil(intl, messages.title)}
          </TextTitleCondensed>
        </h3>

        <TextBodySmaller
          c='paragraph'
          mb
          style={{
            textAlign: isSummary ? 'left' : 'center'
          }}
        >
          {formatMessageIntlUtil(intl, messages.bestFor)}
        </TextBodySmaller>

        {
          // !isCompact && (
          <SubscriptionPremiumPlusPlanFeatures
            isSummary={isSummary}
            areFeaturesPressable={areFeaturesPressable}
            shouldBoldMainFeatures={shouldBoldMainFeatures}
          />
          // )
        }

        <NewCustomerDiscountTimer isCompact={isCompact} />

        <Row
          ac
          mt={2}
          style={{
            fontSize: isSmallMobileScreen ? '0.8rem' : null
          }}
        >
          {priceWithoutDiscountElement}
          <TextBody
            b
          >
            {priceLabelElement}
          </TextBody>
          {priceSuffixElement}
        </Row>

        {
          !!amountSavedPerYear && (
            <View>
              <TextBodySmaller
                fw={500}
                ta={isSummary ? 'left' : 'center'}
              >
                {
                  formatMessageIntlUtil(intl, messages.saveAmountPerYear, {
                    amountSavedPerYear
                  })
                }
              </TextBodySmaller>
            </View>
          )
        }

        {
          !!amountSavedPerMonth && (
            <View>
              <TextBodySmaller
                fw={500}
                ta={isSummary ? 'left' : 'center'}
              >
                {
                  formatMessageIntlUtil(intl, messages.saveAmountPerMonth, {
                    amountSavedPerMonth
                  })
                }
              </TextBodySmaller>
            </View>
          )
        }

        <View>
          <TextBodySmaller
            c='paragraph'
            ta={isSummary ? 'left' : 'center'}
          >
            {formatMessageIntlUtil(intl, freeTrialMessage)}
          </TextBodySmaller>
        </View>

        {
          !isSummary && (
            <TryPremiumPlusFreeButton
              mt={2.5}
              w
            />
          )
        }
      </View>
    </ContentBox>
  )
}

SubscriptionPremiumPlusPlan.propTypes = {
  isReadOnly: PropTypes.bool,
  isSummary: PropTypes.bool
}
